import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import './PaymentPlansStaff.css';

/**
 * STAFF TIME CLOCK - For coaches to clock in/out
 */
function StaffTimeClock({ embedded = false }) {
  const { user } = useAuth();
  const toast = useToast();
  const auth = { headers: { Authorization: `Bearer ${user?.token}` } };
  // Admins (and superadmins) can clock any coach; coaches only themselves.
  const isManager = ['admin', 'superadmin'].includes(user?.role);

  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(user?.id || '');
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [recentEntries, setRecentEntries] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);

  const isSelf = String(selectedStaffId) === String(user?.id);
  const selectedName = isSelf
    ? 'you'
    : (staffList.find(m => String(m.id) === String(selectedStaffId))?.name || 'this coach');

  useEffect(() => {
    loadClasses();
    if (isManager) loadStaffList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedStaffId) { checkClockStatus(); loadRecentEntries(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStaffId]);

  const loadStaffList = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/users?role=coach,admin&active_only=true`, auth);
      setStaffList(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error loading staff:', error);
    }
  };

  const checkClockStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/staff/time-entries/${selectedStaffId}?limit=1`, auth);
      if (response.data.length && response.data[0].status === 'clocked_in') {
        setIsClockedIn(true);
        setCurrentEntry(response.data[0]);
      } else {
        setIsClockedIn(false);
        setCurrentEntry(null);
      }
    } catch (error) {
      console.error('Error checking clock status:', error);
      setIsClockedIn(false);
      setCurrentEntry(null);
    }
  };

  const loadRecentEntries = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/staff/time-entries/${selectedStaffId}?limit=10`, auth);
      setRecentEntries(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading entries:', error);
    }
  };

  const loadClasses = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/classes?teamId=${user?.teamId}`, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Error loading classes:', error);
    }
  };

  const handleClock = async (action) => {
    try {
      await axios.post(`${API_BASE_URL}/api/staff/clock`, {
        userId: selectedStaffId,
        action,
        classId: selectedClass
      }, auth);

      if (action === 'in') {
        setIsClockedIn(true);
        toast.success(isSelf ? 'Clocked in!' : `Clocked in ${selectedName}`);
      } else {
        setIsClockedIn(false);
        setCurrentEntry(null);
        toast.success(isSelf ? 'Clocked out!' : `Clocked out ${selectedName}`);
      }

      checkClockStatus();
      loadRecentEntries();

    } catch (error) {
      console.error('Error clocking:', error);
      toast.error(error.response?.data?.error || ('Failed to clock ' + action));
    }
  };

  const formatDuration = (clockIn, clockOut) => {
    if (!clockOut) return 'In progress...';
    
    const start = new Date(clockIn);
    const end = new Date(clockOut);
    const hours = (end - start) / 1000 / 60 / 60;
    
    return `${hours.toFixed(2)} hours`;
  };

  return (
    <div className="time-clock-container">
      <div className="page-header">
        {!embedded && <h1>🕐 Time Clock</h1>}
        <div className="current-time">
          {new Date().toLocaleTimeString()}
        </div>
      </div>

      {/* Who to clock (admins only) */}
      {isManager && (
        <div className="card">
          <div className="form-group" style={{ margin: 0 }}>
            <label>Clock in/out for</label>
            <select value={selectedStaffId} onChange={(e) => setSelectedStaffId(e.target.value)}>
              <option value={user?.id}>Myself{user?.name ? ` (${user.name})` : ''}</option>
              {staffList
                .filter(m => String(m.id) !== String(user?.id))
                .map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Clock In/Out Card */}
      <div className="card clock-card">
        {!isSelf && (
          <p style={{ margin: '0 0 8px', fontWeight: 600 }}>
            Time card for {selectedName}
          </p>
        )}
        {!isClockedIn ? (
          <div className="clock-in-section">
            <h2>Ready to start your shift?</h2>
            
            <div className="form-group">
              <label>Teaching a class? (Optional)</label>
              <select 
                value={selectedClass || ''}
                onChange={(e) => setSelectedClass(e.target.value || null)}
              >
                <option value="">No specific class</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {cls.class_name}
                  </option>
                ))}
              </select>
            </div>

            <button 
              className="btn btn-large btn-success clock-btn"
              onClick={() => handleClock('in')}
            >
              ▶️ Clock In
            </button>
          </div>
        ) : (
          <div className="clocked-in-section">
            <div className="status-badge">
              <span className="pulse"></span>
              CLOCKED IN
            </div>

            <div className="clock-info">
              <p>Started at: <strong>{currentEntry && new Date(currentEntry.clock_in).toLocaleTimeString()}</strong></p>
              {selectedClass && (
                <p>Class: <strong>{classes.find(c => c.id === selectedClass)?.class_name || 'Unknown'}</strong></p>
              )}
            </div>

            <button 
              className="btn btn-large btn-danger clock-btn"
              onClick={() => handleClock('out')}
            >
              ⏹️ Clock Out
            </button>
          </div>
        )}
      </div>

      {/* Recent Time Entries */}
      <div className="card">
        <h2>Recent Time Entries</h2>

        {recentEntries.length === 0 ? (
          <p className="empty-state">No time entries yet</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Clock In</th>
                <th>Clock Out</th>
                <th>Duration</th>
                <th>Class</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentEntries.map(entry => (
                <tr key={entry.id}>
                  <td>{new Date(entry.clock_in).toLocaleDateString()}</td>
                  <td>{new Date(entry.clock_in).toLocaleTimeString()}</td>
                  <td>
                    {entry.clock_out ? new Date(entry.clock_out).toLocaleTimeString() : '-'}
                  </td>
                  <td>
                    {entry.hours_worked ? `${entry.hours_worked} hrs` : formatDuration(entry.clock_in, entry.clock_out)}
                  </td>
                  <td>{entry.class_name || '-'}</td>
                  <td>
                    <span className={`badge badge-${
                      entry.status === 'approved' ? 'green' :
                      entry.status === 'clocked_in' ? 'blue' :
                      'gray'
                    }`}>
                      {entry.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Weekly Summary */}
      <div className="card">
        <h2>This Week's Summary</h2>
        <div className="hours-summary">
          <div className="summary-stat">
            <div className="stat-value">
              {recentEntries
                .filter(e => e.hours_worked && isThisWeek(e.clock_in))
                .reduce((sum, e) => sum + parseFloat(e.hours_worked), 0)
                .toFixed(2)}
            </div>
            <div className="stat-label">Hours Worked</div>
          </div>
          <div className="summary-stat">
            <div className="stat-value">
              {recentEntries.filter(e => e.clock_out && isThisWeek(e.clock_in)).length}
            </div>
            <div className="stat-label">Shifts Completed</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function isThisWeek(date) {
  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay());
  weekStart.setHours(0, 0, 0, 0);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);
  const checkDate = new Date(date);
  return checkDate >= weekStart && checkDate <= weekEnd;
}

export default StaffTimeClock;
